import { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { webTapFloor, type UiTheme } from "../theme";
import { useLiveAnnouncement } from "./useLiveAnnouncement";

// <Toast> + useToast (#336): one line that says what just happened and goes
// away by itself, with at most one action. ⛔ Never a question: the timer
// would answer it. Spoken through useLiveAnnouncement (a region mounted with
// its text is not read, DS-72). Absolute at the bottom of its parent.

const DEFAULT_MS = 5000;

export type ToastAction = { label: string; onPress: () => void };
type ToastState = { id: number; message: string; action?: ToastAction };

export function useToast(timeoutMs = DEFAULT_MS) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const seq = useRef(0);
  const live = useLiveAnnouncement();
  const { speak } = live;
  const show = useCallback(
    (message: string, action?: ToastAction) => {
      seq.current += 1;
      setToast({ id: seq.current, message, action });
      speak(message);
    },
    [speak],
  );
  const dismiss = useCallback(() => setToast(null), []);
  useEffect(() => {
    if (!toast) return;
    const id = toast.id;
    const h = setTimeout(
      () => setToast((cur) => (cur?.id === id ? null : cur)),
      timeoutMs,
    );
    return () => clearTimeout(h);
  }, [toast, timeoutMs]);
  return { toast, show, dismiss, region: live.region };
}

export type ToastProps = {
  theme: UiTheme;
  toast: ToastState | null;
  onDismiss: () => void;
  /** Extra room under the toast, for a bar pinned at the bottom of the same
   *  parent (#336: the program editor's selection bar). Default 0. */
  bottomInset?: number;
};

export function Toast({ theme, toast, onDismiss, bottomInset = 0 }: ToastProps) {
  const styles = makeStyles(theme);
  if (!toast) return null;
  const { action } = toast;
  return (
    <View
      style={[styles.wrap, bottomInset > 0 && { bottom: theme.spacing.s5 + bottomInset }]}
      pointerEvents="box-none"
    >
      <View style={styles.box}>
        {/* Already spoken by the live region: hidden here so it is heard once.
            The action stays reachable. */}
        <Text
          style={styles.text}
          importantForAccessibility="no"
          accessibilityElementsHidden
        >
          {toast.message}
        </Text>
        {action ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={action.label}
            hitSlop={4}
            onPress={() => {
              onDismiss();
              action.onPress();
            }}
            style={styles.action}
          >
            <Text style={styles.actionText}>{action.label}</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: {
      position: "absolute",
      left: s.s4,
      right: s.s4,
      bottom: s.s5,
      alignItems: "center",
    },
    box: {
      width: "100%",
      maxWidth: 480,
      flexDirection: "row",
      alignItems: "center",
      gap: s.s4,
      backgroundColor: p.ink,
      borderRadius: r.r2,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    text: { flex: 1, color: p.bg, fontSize: ty.sizes.base },
    action: {
      minHeight: webTapFloor ?? 36,
      justifyContent: "center",
      paddingHorizontal: s.s4,
      borderWidth: 1,
      borderColor: p.bg,
      borderRadius: r.r1,
    },
    actionText: { color: p.bg, fontSize: ty.sizes.base, fontWeight: "600" },
  });
}
